import { useCallback, useEffect, useState } from 'react';
import { PortailFacturationError, chargerPacks, euros, payerPack, storeDe } from './billing.js';

/** Paramètre ajouté au retour du paiement d'un pack (cf. `retourPack`). */
const PARAM_RETOUR = 'pack_session';

const nombreFr = (n) => new Intl.NumberFormat('fr-FR').format(Number(n) || 0);
const dateFr = (iso) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** Retour du paiement : la page elle-même, marquée pour annoncer le crédit. */
function retourPack() {
  const url = new URL(window.location.href);
  url.searchParams.delete(PARAM_RETOUR);
  url.searchParams.set(PARAM_RETOUR, '{CHECKOUT_SESSION_ID}');
  // `{` et `}` ne doivent pas être encodés : Stripe remplace le gabarit tel quel.
  return url.toString().replace(encodeURIComponent('{CHECKOUT_SESSION_ID}'), '{CHECKOUT_SESSION_ID}');
}

/**
 * Packs du produit (04/10/2026), dans « Mon abonnement » : ce qui est en vente,
 * les crédits restants, les achats de l'espace.
 *
 * Un pack s'achète en une fois, avec ou sans abonnement. Ses crédits se
 * consomment une fois le quota du mois de la formule épuisé ; un pack de
 * capacité relève un plafond (dossiers, stockage…). Le paiement est celui de
 * l'abonnement : la même fenêtre Stripe embarquée (`onPaiement`).
 */
export function SectionPacks({ apiUrl, getToken, product, onPaiement, onChange, ventesFermees = false, spaceId = null }) {
  const [etat, setEtat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [nombres, setNombres] = useState({});
  const [enCours, setEnCours] = useState(null);
  const [annonce, setAnnonce] = useState(null);

  const charger = useCallback(async () => {
    try {
      setEtat(await chargerPacks(apiUrl, getToken, product, spaceId));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof PortailFacturationError ? e.message : 'Impossible de charger les packs.');
    }
  }, [apiUrl, getToken, product, spaceId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // Retour d'un paiement : le webhook crédite le pack en quelques secondes.
  // On l'annonce, on relit quelques fois, et on nettoie l'adresse.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(PARAM_RETOUR)) return undefined;
    url.searchParams.delete(PARAM_RETOUR);
    window.history.replaceState(null, '', url.toString());
    setAnnonce('Paiement reçu : votre pack est crédité dans quelques instants.');
    let restant = 5;
    const minuteur = setInterval(() => {
      restant -= 1;
      void charger().then(() => onChange?.());
      if (restant <= 0) clearInterval(minuteur);
    }, 2000);
    return () => clearInterval(minuteur);
  }, [charger, onChange]);

  async function acheter(pack) {
    const nombre = Math.min(20, Math.max(1, Number(nombres[pack.id] ?? 1) || 1));
    setEnCours(pack.id);
    setErreur(null);
    try {
      const session = await payerPack(apiUrl, getToken, pack.id, { nombre, returnUrl: retourPack() }, spaceId);
      onPaiement?.(session.clientSecret);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Impossible d’ouvrir le paiement.');
    } finally {
      setEnCours(null);
    }
  }

  if (!etat && !erreur) return null;
  const packs = etat?.packs ?? [];
  const achats = etat?.achats ?? [];
  const credits = Object.entries(etat?.credits ?? {}).filter(([, n]) => n > 0);
  // Rien en vente, rien acheté : la section n'a rien à dire.
  if (etat && packs.length === 0 && achats.length === 0) return null;

  const libelleQuota = (cle) =>
    packs.find((p) => p.feature.key === cle)?.feature.label ?? achats.find((a) => a.featureKey === cle)?.libelle ?? cle;

  return (
    <div className="card abonnement__carte abonnement__packs">
      <h2>Packs</h2>
      <p className="abonnement__aide">
        À l’unité, sans engagement. Les crédits servent une fois le quota du mois de votre formule épuisé,
        et restent jusqu’à leur échéance.
      </p>
      {annonce && (
        <p className="alert alert-success" role="status">
          {annonce}
        </p>
      )}
      {erreur && (
        <p className="alert alert-error" role="alert">
          {erreur}
        </p>
      )}

      {credits.length > 0 && (
        <p className="abonnement__ligne">
          <strong>Crédits restants :</strong>
          {credits.map(([cle, n]) => (
            <span key={cle}>
              {nombreFr(n)} — {libelleQuota(cle)}
            </span>
          ))}
        </p>
      )}

      {packs.length > 0 && (
        <ul className="abonnement__packs-liste">
          {packs.map((pack) => (
            <li key={pack.id} className="abonnement__pack">
              <div>
                <span className="abonnement__formule-nom">{pack.name}</span>
                <p className="abonnement__aide">
                  +{nombreFr(pack.quantite)} {pack.feature.label}
                  {pack.nature === 'CAPACITE' ? ' (plafond relevé)' : ''}
                  {' · '}
                  {pack.validiteMois ? `valable ${pack.validiteMois} mois` : 'sans limite de durée'}
                </p>
                {pack.description && <p className="abonnement__aide">{pack.description}</p>}
              </div>
              <div className="abonnement__pack-achat">
                <span className="abonnement__formule-prix">{euros(pack.prix)}</span>
                {etat?.peutAcheter && !ventesFermees ? (
                  <>
                    <label className="abonnement__pack-nombre">
                      <span className="sr-only">Nombre de « {pack.name} »</span>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={nombres[pack.id] ?? '1'}
                        onChange={(e) => setNombres({ ...nombres, [pack.id]: e.target.value })}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={enCours !== null}
                      onClick={() => void acheter(pack)}
                    >
                      {enCours === pack.id ? 'Chargement…' : 'Acheter'}
                    </button>
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {etat && !etat.peutAcheter && packs.length > 0 && (
        <p className="abonnement__aide">Le propriétaire ou un administrateur de l’espace peut acheter des packs.</p>
      )}

      {achats.length > 0 && (
        <details>
          <summary>Mes achats ({achats.length})</summary>
          <ul className="abonnement__packs-achats">
            {achats.map((a) => (
              <li key={a.id}>
                <strong>{a.libelle}</strong> — {dateFr(a.acheteLe)}
                {a.source === 'OFFERT' ? ' (offert)' : ''}
                {storeDe(a.source) ? ` (appli mobile, ${storeDe(a.source).nom})` : ''}
                {' · '}
                {a.rembourse
                  ? 'remboursé'
                  : a.nature === 'CREDITS'
                    ? `${nombreFr(a.restant ?? 0)} / ${nombreFr(a.quantite)} restant(s)`
                    : `+${nombreFr(a.quantite)}`}
                {!a.rembourse && a.expireLe ? ` · ${a.valide ? 'jusqu’au' : 'expiré le'} ${dateFr(a.expireLe)}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

export default SectionPacks;
